/**
 * Where a bill of materials' goods line comes from (piece 18; decision 3320,
 * T2276): bought on the purchase order, or taken from our own shelf and picked
 * to the Deal. The rules live here so the server and the editor read one copy.
 */

/** 🔒 The two sources a goods line takes, Buy by default (decision 3320). */
export const BOM_LINE_SOURCES = ['Buy', 'FromStock'] as const;
export type BomLineSource = (typeof BOM_LINE_SOURCES)[number];

/** A source as the picker offers it and the line reads it (decision 3320). */
export const BOM_LINE_SOURCE_LABELS: Readonly<Record<BomLineSource, string>> = {
  Buy: 'Buy',
  FromStock: 'From Stock',
};

export function isBomLineSource(value: unknown): value is BomLineSource {
  return typeof value === 'string' && (BOM_LINE_SOURCES as readonly string[]).includes(value);
}

/**
 * 🔨 What refuses a source before anything is written (decision 3320): a value
 * outside the two, and From Stock on a Service line, which no shelf holds.
 * Absent is no change and refuses nothing.
 */
export function bomSourceProblem(source: unknown, isService: boolean): string | null {
  if (source === undefined || source === null) return null;
  if (!isBomLineSource(source)) return 'A bill of materials line is bought or taken from stock.';
  if (source === 'FromStock' && isService) return 'A Service line holds no stock, so it is never taken from stock.';
  return null;
}

/** One of our sites holding the Item, as the line's site picker offers it (decision 3321). */
export interface BomStockSite {
  siteId: string;
  siteName: string;
  /** On hand at the site, in the Item's own unit. */
  onHand: number;
}

/**
 * The shelf beside a goods line, as the Item Table draws it under the line
 * (decisions 3320, 3321): what our sites hold of the Item, and on a From Stock
 * line the site it is reserved at, what is reserved, what has been picked and
 * what the shelf cannot cover. Every figure is in the Item's own unit. A
 * Service line carries none.
 */
export interface BomLineStock {
  /** On hand across our sites, in the Item's own unit; in transit is on hand nowhere (decision 0980). */
  onHand: number;
  /** The Item's own unit, which every figure here is in. */
  unitName: string;
  /** 🔒 The site a From Stock line is reserved and picked at (decision 3321); null on a Buy line and where no site holds the Item. */
  siteId: string | null;
  siteName: string | null;
  /** What that site holds, which the pick is refused above. */
  onHandAtSite: number;
  /** Our sites holding the Item, most first, with the line's own site among them. */
  sites: readonly BomStockSite[];
  /** 🔒 The open claims naming the line (decision 3321). */
  reserved: number;
  /** 🔒 What has been picked to a Deal for the line, less what came back (decision 3322). */
  picked: number;
  /** 🔒 What the line still needs that its claim does not cover, on a From Stock line; zero on a Buy line. */
  short: number;
}

/**
 * 🔒 The site a line is reserved at when it is marked From Stock and names
 * none (decision 3321): the site holding the most of the Item. Null where no
 * site holds any.
 */
export function defaultStockSite(sites: readonly { siteId: string; onHand: number }[]): string | null {
  let best: { siteId: string; onHand: number } | null = null;
  for (const site of sites) if (site.onHand > 0 && (best === null || site.onHand > best.onHand)) best = site;
  return best?.siteId ?? null;
}

const four = (value: number) => Math.round(value * 10_000) / 10_000;

/**
 * 🔒 How a From Stock line's claim moves to its need (decision 3321). The need
 * is the line's quantity, in the Item's unit, less what has been picked. The
 * hold is the need, capped at what the site has free for this line: on hand
 * there less every other open claim, the line's own excluded. A claim above
 * the hold gives back the difference, which is how a pick lowers it and how a
 * shelf that fell below the claim trims it; a claim below the hold takes the
 * rest. What the hold cannot cover is the line's shortfall, need less hold.
 */
export function reservationStep(facts: { quantity: number; picked: number; claimed: number; free: number }): { release: number; claim: number; short: number } {
  const need = four(Math.max(0, facts.quantity - facts.picked));
  const hold = four(Math.min(need, Math.max(0, facts.free)));
  return { release: four(Math.max(0, facts.claimed - hold)), claim: four(Math.max(0, hold - facts.claimed)), short: four(need - hold) };
}
